import React, { useState, useEffect } from 'react';
import { 
  CloudArrowUpIcon,
  Cog6ToothIcon,
  PlayIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  ServerIcon,
  CircleStackIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';
import { useServerAPI, useServerMutation } from '../hooks/useServerData';
import { useServer } from '../contexts/ServerContext';
import ModularBackupConfig from '../components/ModularBackupConfig';
import EmailTemplateSelector from '../components/EmailTemplateSelector';

const BackupConfig = () => {
  const { selectedServer, getCurrentServer } = useServer();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [activeTab, setActiveTab] = useState('traditional'); // 'traditional' ou 'modular'
  const [formData, setFormData] = useState({
    serverId: '',
    awsRegion: 'us-east-1',
    awsBucket: '',
    awsAccessKey: '',
    awsSecretKey: '',
    enabled: true,
    schedule: '0 2 * * *',
    retentionDays: 30,
    backupPostgresql: true,
    backupPortainer: true,
    compressBackups: true,
    notifyOnSuccess: false,
    notifyOnFailure: true,
    notificationWebhook: '',
    emailEnabled: true,
    emailTo: '',
    emailFrom: '',
    smtpHost: '',
    smtpPort: 587,
    smtpUser: '',
    smtpPassword: '',
    smtpUseSSL: true,
    emailTemplate: '',
    // Configurações PostgreSQL
    pgHost: 'ptbd01.viptecnologia.com.br',
    pgPort: 5485,
    pgUser: 'postgres',
    pgPassword: '',
    // Configurações avançadas
    createDataBackup: true,
    createSchemaBackup: true,
    createConsolidatedBackup: true,
    localBackupDir: '/root/backups/postgresql',
    localPortainerDir: '/root/backups/portainer',
    useS3DateFolders: true,
    s3PostgresqlPath: 'postgresql',
    s3PortainerPath: 'portainer',
    removeLocalAfterS3Upload: true,
    autoCleanS3: true,
    portainerDataPath: '/var/lib/docker/volumes/portainer_data/_data',
    includePortainerStacks: true,
    includePortainerCerts: true,
    includePortainerDatabase: true,
    databases: []
  });

  // Fetch configuração do servidor atual
  const { data: configData, loading, error, refresh } = useServerAPI(
    `/api/backup-config/server/${selectedServer}`,
    { 
      refetchInterval: 30000,
      onError: (err) => {
        if (err?.message?.includes('404')) {
          // Configuração não existe - não é erro
          return;
        }
      }
    }
  );

  // Extrair a configuração da resposta da API
  const config = configData?.config;

  // Fetch bancos de dados disponíveis
  const [availableDatabases, setAvailableDatabases] = useState([]);
  const [loadingDatabases, setLoadingDatabases] = useState(false);

  // Fetch databases separately since the endpoint already has serverId in path
  useEffect(() => {
    const fetchDatabases = async () => {
      if (!selectedServer) return;
      
      setLoadingDatabases(true);
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`/api/backup-config/server/${selectedServer}/databases`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          setAvailableDatabases(data.databases || []);
        } else {
          console.error('Erro ao carregar bancos:', await response.text());
          setAvailableDatabases([]);
        }
      } catch (error) {
        console.error('Erro ao buscar bancos de dados:', error);
        setAvailableDatabases([]);
      } finally {
        setLoadingDatabases(false);
      }
    };

    fetchDatabases();
  }, [selectedServer]);

  // Fetch estatísticas
  const { data: statsData } = useServerAPI(
    `/api/backup-config/stats?serverId=${selectedServer}`,
    { refetchInterval: 30000 }
  );

  // Fetch jobs recentes
  const { data: jobsData } = useServerAPI(
    `/api/backup-config/jobs?serverId=${selectedServer}&limit=10`,
    { refetchInterval: 15000 }
  );

  const { mutate } = useServerMutation();

  useEffect(() => {
    if (config) {
      setFormData({
        ...config,
        awsAccessKey: '', // Por segurança, não pré-popular
        awsSecretKey: '',
        smtpUser: '', // Por segurança, não pré-popular
        smtpPassword: '',
        pgPassword: '', // Por segurança, não pré-popular
        databases: config.databases || []
      });
    }
  }, [config]);

  useEffect(() => {
    if (availableDatabases && Array.isArray(availableDatabases) && !config) {
      setFormData(prev => ({
        ...prev,
        serverId: selectedServer,
        databases: availableDatabases.map(db => ({ ...db, enabled: true }))
      }));
    }
  }, [availableDatabases, config, selectedServer]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingConfig) {
        await mutate(`/api/backup-config/${editingConfig.id}`, formData, 'PUT');
      } else {
        await mutate('/api/backup-config', formData, 'POST');
      }
      
      setIsModalOpen(false);
      setEditingConfig(null);
      refresh();
    } catch (error) {
      console.error('Erro ao salvar configuração:', error);
    }
  };

  const handleTest = async () => {
    if (!config) return;
    
    try {
      const result = await mutate(`/api/backup-config/${config.id}/test`, {}, 'POST');
      alert('Teste de conexão AWS realizado com sucesso!');
    } catch (error) {
      alert(`Erro no teste: ${error.message}`);
    }
  };

  const handleBackup = async () => {
    if (!config) return;
    
    try {
      const result = await mutate(`/api/backup-config/${config.id}/backup`, {}, 'POST');
      alert('Backup iniciado! Verifique a aba de Jobs para acompanhar o progresso.');
      refresh();
    } catch (error) {
      alert(`Erro ao iniciar backup: ${error.message}`);
    }
  };

  const handleDelete = async () => {
    if (!config || !window.confirm('Tem certeza que deseja remover esta configuração?')) return;
    
    try {
      await mutate(`/api/backup-config/${config.id}`, {}, 'DELETE');
      refresh();
    } catch (error) {
      alert(`Erro ao remover configuração: ${error.message}`);
    }
  };

  const formatSchedule = (cron) => {
    const scheduleMap = {
      // Diários
      '0 0 * * *': 'Diariamente às 00:00',
      '0 1 * * *': 'Diariamente às 01:00',
      '0 2 * * *': 'Diariamente às 02:00',
      '0 3 * * *': 'Diariamente às 03:00',
      '0 4 * * *': 'Diariamente às 04:00',
      '0 5 * * *': 'Diariamente às 05:00',
      '0 6 * * *': 'Diariamente às 06:00',
      '0 12 * * *': 'Diariamente às 12:00',
      '0 18 * * *': 'Diariamente às 18:00',
      '0 20 * * *': 'Diariamente às 20:00',
      '0 22 * * *': 'Diariamente às 22:00',
      '0 23 * * *': 'Diariamente às 23:00',
      
      // Múltiplas vezes
      '0 */6 * * *': 'A cada 6 horas',
      '0 */12 * * *': 'A cada 12 horas',
      '0 6,18 * * *': 'Duas vezes ao dia (06:00 e 18:00)',
      
      // Semanais
      '0 0 * * 0': 'Domingo à meia-noite',
      '0 2 * * 0': 'Domingo às 02:00',
      '0 0 * * 1': 'Segunda-feira à meia-noite',
      '0 2 * * 1': 'Segunda-feira às 02:00',
      '0 0 * * 6': 'Sábado à meia-noite',
      '0 2 * * 6': 'Sábado às 02:00',
      
      // Mensais
      '0 0 1 * *': 'Mensalmente (Dia 1 à meia-noite)',
      '0 2 1 * *': 'Mensalmente (Dia 1 às 02:00)',
      '0 0 15 * *': 'Mensalmente (Dia 15 à meia-noite)',
      '0 2 15 * *': 'Mensalmente (Dia 15 às 02:00)'
    };
    return scheduleMap[cron] || `Personalizado: ${cron}`;
  };

  const currentServer = getCurrentServer();

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Configuração de Backup</h1>
          <p className="text-gray-600 mt-1">
            Servidor: <span className="font-medium">{currentServer?.name || 'Carregando...'}</span>
          </p>
        </div>
        
        <div className="flex space-x-3">
          {config && config.id && (
            <>
              <button
                onClick={handleTest}
                className="btn-secondary flex items-center space-x-2"
              >
                <ShieldCheckIcon className="h-4 w-4" />
                <span>Testar Conexão</span>
              </button>
              
              <button
                onClick={handleBackup}
                className="btn-primary flex items-center space-x-2"
                disabled={!config.enabled}
              >
                <PlayIcon className="h-4 w-4" />
                <span>Backup Manual</span>
              </button>
            </>
          )}
          
          <button
            onClick={() => {
              setEditingConfig(config);
              setIsModalOpen(true);
            }}
            className="btn-primary flex items-center space-x-2"
          >
            <Cog6ToothIcon className="h-4 w-4" />
            <span>{config ? 'Editar' : 'Configurar'}</span>
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('traditional')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'traditional'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center space-x-2">
              <Cog6ToothIcon className="h-4 w-4" />
              <span>Configuração Tradicional</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('modular')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'modular'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center space-x-2">
              <CircleStackIcon className="h-4 w-4" />
              <span>Configuração Modular</span>
            </div>
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'modular' ? (
        <ModularBackupConfig serverId={currentServer?.id || 'local'} />
      ) : (
        <>

      {error && !error?.message?.includes('404') && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Erro</h3>
              <p className="text-sm text-red-700 mt-1">{error?.message || 'Erro desconhecido'}</p>
            </div>
          </div>
        </div>
      )}

      {/* Status da Configuração */}
      {config && config.id ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Configuração Atual */}
          <div className="card col-span-2">
            <div className="flex justify-between items-start mb-4">
              <h2 className="text-lg font-medium text-gray-900">Configuração Atual</h2>
              <div className="flex space-x-2">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  config.enabled 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {config.enabled ? 'Ativo' : 'Inativo'}
                </span>
                
                <button
                  onClick={handleDelete}
                  className="text-red-600 hover:text-red-800"
                  title="Remover configuração"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Bucket AWS</label>
                  <p className="text-gray-900">{config.awsBucket}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Região</label>
                  <p className="text-gray-900">{config.awsRegion}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Agendamento</label>
                  <p className="text-gray-900">{formatSchedule(config.schedule)}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Retenção</label>
                  <p className="text-gray-900">{config.retentionDays} dias</p>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">Tipos de Backup</label>
                <div className="flex space-x-4 mt-1">
                  {config.backupPostgresql && (
                    <span className="flex items-center text-sm text-green-600">
                      <CircleStackIcon className="h-4 w-4 mr-1" />
                      PostgreSQL
                    </span>
                  )}
                  {config.backupPortainer && (
                    <span className="flex items-center text-sm text-green-600">
                      <ServerIcon className="h-4 w-4 mr-1" />
                      Portainer
                    </span>
                  )}
                </div>
              </div>

              {config.databases && config.databases.length > 0 && (
                <div>
                  <label className="text-sm font-medium text-gray-700">Bancos Selecionados</label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {config.databases && Array.isArray(config.databases) ? config.databases.filter(db => db.enabled).map((db, index) => (
                      <span key={index} className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
                        {db.databaseName}
                      </span>
                    )) : null}
                  </div>
                </div>
              )}

              {config.backupPostgresql && config.pgHost && (
                <div>
                  <label className="text-sm font-medium text-gray-700">Configuração PostgreSQL</label>
                  <div className="grid grid-cols-2 gap-4 mt-1">
                    <div>
                      <p className="text-xs text-gray-500">Servidor:</p>
                      <p className="text-gray-900 text-sm">{config.pgHost}:{config.pgPort}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Usuário:</p>
                      <p className="text-gray-900 text-sm">{config.pgUser}</p>
                    </div>
                  </div>
                  <div className="flex space-x-4 mt-2 text-xs">
                    {config.createDataBackup && (
                      <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded">Dados</span>
                    )}
                    {config.createSchemaBackup && (
                      <span className="bg-green-100 text-green-800 px-2 py-1 rounded">Schema</span>
                    )}
                    {config.createConsolidatedBackup && (
                      <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded">Consolidado</span>
                    )}
                  </div>
                </div>
              )}

              {config.emailEnabled && (
                <div>
                  <label className="text-sm font-medium text-gray-700">Configuração de Email</label>
                  <div className="grid grid-cols-2 gap-4 mt-1">
                    <div>
                      <p className="text-xs text-gray-500">Para:</p>
                      <p className="text-gray-900 text-sm">{config.emailTo}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">SMTP:</p>
                      <p className="text-gray-900 text-sm">{config.smtpHost}:{config.smtpPort}</p>
                    </div>
                  </div>
                  <div className="flex space-x-4 mt-2">
                    {config.notifyOnSuccess && (
                      <span className="flex items-center text-sm text-green-600">
                        <CheckCircleIcon className="h-4 w-4 mr-1" />
                        Sucesso
                      </span>
                    )}
                    {config.notifyOnFailure && (
                      <span className="flex items-center text-sm text-red-600">
                        <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
                        Falha
                      </span>
                    )}
                  </div>
                </div>
              )}

              {(config.useS3DateFolders || config.autoCleanS3 || config.removeLocalAfterS3Upload) && (
                <div>
                  <label className="text-sm font-medium text-gray-700">Configurações Avançadas</label>
                  <div className="flex flex-wrap gap-2 mt-1 text-xs">
                    {config.useS3DateFolders && (
                      <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded">Pastas por data</span>
                    )}
                    {config.removeLocalAfterS3Upload && (
                      <span className="bg-orange-100 text-orange-800 px-2 py-1 rounded">Remove local</span>
                    )}
                    {config.autoCleanS3 && (
                      <span className="bg-red-100 text-red-800 px-2 py-1 rounded">Limpeza S3</span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Estatísticas */}
          <div className="card">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Estatísticas</h2>
            
            {statsData?.stats ? (
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Total de Jobs:</span>
                  <span className="font-medium">{statsData.stats.total || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Sucessos:</span>
                  <span className="font-medium text-green-600">{statsData.stats.completed || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Falhas:</span>
                  <span className="font-medium text-red-600">{statsData.stats.failed || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Executando:</span>
                  <span className="font-medium text-blue-600">{statsData.stats.running || 0}</span>
                </div>
              </div>
            ) : (
              <p className="text-gray-500 text-sm">Nenhuma estatística disponível</p>
            )}
          </div>
        </div>
      ) : (
        /* Sem configuração */
        <div className="text-center py-12">
          <CloudArrowUpIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhuma configuração encontrada</h3>
          <p className="text-gray-600 mb-6">Configure o backup automático para este servidor.</p>
          <button
            onClick={() => setIsModalOpen(true)}
            className="btn-primary"
          >
            Criar Configuração
          </button>
        </div>
      )}

      {/* Jobs Recentes */}
      {jobsData?.jobs && jobsData.jobs.length > 0 && (
        <div className="card">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Jobs Recentes</h2>
          
          <div className="space-y-3">
            {jobsData.jobs && Array.isArray(jobsData.jobs) ? jobsData.jobs.map((job) => (
              <div key={job.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className={`w-3 h-3 rounded-full ${
                    job.status === 'completed' ? 'bg-green-500' :
                    job.status === 'failed' ? 'bg-red-500' :
                    job.status === 'running' ? 'bg-blue-500' :
                    'bg-yellow-500'
                  }`}></div>
                  
                  <div>
                    <p className="font-medium text-gray-900">
                      Job {job.jobId?.substring(0, 8)}
                    </p>
                    <p className="text-sm text-gray-600">
                      {job.type === 'manual' ? 'Manual' : 'Agendado'} • 
                      {new Date(job.createdAt).toLocaleString('pt-BR')}
                    </p>
                  </div>
                </div>
                
                <div className="text-right">
                  <span className={`text-sm font-medium ${
                    job.status === 'completed' ? 'text-green-600' :
                    job.status === 'failed' ? 'text-red-600' :
                    job.status === 'running' ? 'text-blue-600' :
                    'text-yellow-600'
                  }`}>
                    {job.status === 'completed' ? 'Concluído' :
                     job.status === 'failed' ? 'Falhou' :
                     job.status === 'running' ? 'Executando' :
                     'Pendente'}
                  </span>
                  
                  {job.duration && (
                    <p className="text-xs text-gray-500">
                      {Math.floor(job.duration / 60)}m {job.duration % 60}s
                    </p>
                  )}
                </div>
              </div>
            )) : null}
          </div>
        </div>
      )}

      {/* Modal de Configuração */}
      {isModalOpen && (
        <BackupConfigModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setEditingConfig(null);
          }}
          formData={formData}
          setFormData={setFormData}
          onSubmit={handleSubmit}
          availableDatabases={availableDatabases}
          loadingDatabases={loadingDatabases}
          isEditing={!!editingConfig}
          selectedServer={selectedServer}
          mutate={mutate}
        />
      )}
        </>
      )}
    </div>
  );
};

// Modal de Configuração (componente separado devido ao tamanho)
const BackupConfigModal = ({ 
  isOpen, 
  onClose, 
  formData, 
  setFormData, 
  onSubmit, 
  availableDatabases, 
  loadingDatabases,
  isEditing,
  selectedServer,
  mutate
}) => {
  if (!isOpen) return null;

  const handleDatabaseToggle = (dbName, field, value) => {
    setFormData(prev => ({
      ...prev,
      databases: (prev.databases || []).map(db => 
        db.name === dbName || db.databaseName === dbName 
          ? { ...db, [field]: value }
          : db
      )
    }));
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose}></div>

        <div className="inline-block w-full max-w-4xl p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-lg">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-gray-900">
              {isEditing ? 'Editar' : 'Nova'} Configuração de Backup
            </h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <span className="sr-only">Fechar</span>
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={onSubmit} className="space-y-6">
            {/* AWS Configuration */}
            <div className="card">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Configurações AWS S3</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Bucket S3 *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.awsBucket}
                    onChange={(e) => setFormData({...formData, awsBucket: e.target.value})}
                    className="input-field"
                    placeholder="meu-bucket-backup"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Região AWS
                  </label>
                  <select
                    value={formData.awsRegion}
                    onChange={(e) => setFormData({...formData, awsRegion: e.target.value})}
                    className="input-field"
                  >
                    <option value="us-east-1">us-east-1 (N. Virginia)</option>
                    <option value="us-west-1">us-west-1 (N. California)</option>
                    <option value="us-west-2">us-west-2 (Oregon)</option>
                    <option value="eu-west-1">eu-west-1 (Ireland)</option>
                    <option value="sa-east-1">sa-east-1 (São Paulo)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Access Key ID *
                  </label>
                  <input
                    type="password"
                    required={!isEditing}
                    value={formData.awsAccessKey}
                    onChange={(e) => setFormData({...formData, awsAccessKey: e.target.value})}
                    className="input-field"
                    placeholder={isEditing ? "Deixe em branco para manter atual" : "AKIAIOSFODNN7EXAMPLE"}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Secret Access Key *
                  </label>
                  <input
                    type="password"
                    required={!isEditing}
                    value={formData.awsSecretKey}
                    onChange={(e) => setFormData({...formData, awsSecretKey: e.target.value})}
                    className="input-field"
                    placeholder={isEditing ? "Deixe em branco para manter atual" : "wJalrXUtnFEMI/K7MDENG/bPxRfiCYEXAMPLEKEY"}
                  />
                </div>
              </div>

              {/* Botão de teste */}
              <div className="mt-4 flex justify-end">
                <button
                  type="button"
                  onClick={async () => {
                    if (!formData.awsBucket || !formData.awsAccessKey || !formData.awsSecretKey) {
                      alert('Por favor, preencha todos os campos AWS antes de testar.');
                      return;
                    }

                    try {
                      const result = await mutate('/api/backup-config/test', {
                        serverId: formData.serverId || selectedServer,
                        awsAccessKey: formData.awsAccessKey,
                        awsSecretKey: formData.awsSecretKey,
                        awsRegion: formData.awsRegion,
                        awsBucket: formData.awsBucket
                      }, 'POST');
                      alert('✅ Conexão AWS testada com sucesso!');
                    } catch (error) {
                      alert(`❌ Erro no teste: ${error.message || 'Falha na conexão'}`);
                    }
                  }}
                  className="btn-secondary flex items-center space-x-2"
                  disabled={!formData.awsBucket || !formData.awsAccessKey || !formData.awsSecretKey}
                >
                  <ShieldCheckIcon className="h-4 w-4" />
                  <span>Testar Conexão AWS</span>
                </button>
              </div>
            </div>

            {/* Schedule Configuration */}
            <div className="card">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Agendamento</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Horário (Cron)
                  </label>
                  <select
                    value={formData.schedule}
                    onChange={(e) => setFormData({...formData, schedule: e.target.value})}
                    className="input-field"
                  >
                    <optgroup label="Diariamente">
                      <option value="0 0 * * *">Diariamente às 00:00 (meia-noite)</option>
                      <option value="0 1 * * *">Diariamente às 01:00</option>
                      <option value="0 2 * * *">Diariamente às 02:00</option>
                      <option value="0 3 * * *">Diariamente às 03:00</option>
                      <option value="0 4 * * *">Diariamente às 04:00</option>
                      <option value="0 5 * * *">Diariamente às 05:00</option>
                      <option value="0 6 * * *">Diariamente às 06:00</option>
                      <option value="0 12 * * *">Diariamente às 12:00 (meio-dia)</option>
                      <option value="0 18 * * *">Diariamente às 18:00</option>
                      <option value="0 20 * * *">Diariamente às 20:00</option>
                      <option value="0 22 * * *">Diariamente às 22:00</option>
                      <option value="0 23 * * *">Diariamente às 23:00</option>
                    </optgroup>
                    <optgroup label="Múltiplas vezes por dia">
                      <option value="0 */6 * * *">A cada 6 horas</option>
                      <option value="0 */12 * * *">A cada 12 horas</option>
                      <option value="0 6,18 * * *">Duas vezes (06:00 e 18:00)</option>
                    </optgroup>
                    <optgroup label="Semanalmente">
                      <option value="0 0 * * 0">Domingo à meia-noite</option>
                      <option value="0 2 * * 0">Domingo às 02:00</option>
                      <option value="0 0 * * 1">Segunda-feira à meia-noite</option>
                      <option value="0 2 * * 1">Segunda-feira às 02:00</option>
                      <option value="0 0 * * 6">Sábado à meia-noite</option>
                      <option value="0 2 * * 6">Sábado às 02:00</option>
                    </optgroup>
                    <optgroup label="Mensalmente">
                      <option value="0 0 1 * *">Dia 1 à meia-noite</option>
                      <option value="0 2 1 * *">Dia 1 às 02:00</option>
                      <option value="0 0 15 * *">Dia 15 à meia-noite</option>
                      <option value="0 2 15 * *">Dia 15 às 02:00</option>
                    </optgroup>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Retenção (dias)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="365"
                    value={formData.retentionDays}
                    onChange={(e) => setFormData({...formData, retentionDays: parseInt(e.target.value)})}
                    className="input-field"
                  />
                </div>
              </div>

              <div className="mt-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.enabled}
                    onChange={(e) => setFormData({...formData, enabled: e.target.checked})}
                    className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                  />
                  <span className="ml-2 text-sm text-gray-700">Backup ativo</span>
                </label>
              </div>
            </div>

            {/* Backup Types */}
            <div className="card">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Tipos de Backup</h3>
              
              <div className="space-y-3">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.backupPostgresql}
                    onChange={(e) => setFormData({...formData, backupPostgresql: e.target.checked})}
                    className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                  />
                  <span className="ml-2 text-sm text-gray-700">Backup PostgreSQL</span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.backupPortainer}
                    onChange={(e) => setFormData({...formData, backupPortainer: e.target.checked})}
                    className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                  />
                  <span className="ml-2 text-sm text-gray-700">Backup Portainer</span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.compressBackups}
                    onChange={(e) => setFormData({...formData, compressBackups: e.target.checked})}
                    className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                  />
                  <span className="ml-2 text-sm text-gray-700">Comprimir backups</span>
                </label>
              </div>
            </div>

            {/* PostgreSQL Configuration */}
            {formData.backupPostgresql && (
              <div className="card">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Configurações PostgreSQL</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Host PostgreSQL *
                    </label>
                    <input
                      type="text"
                      required={formData.backupPostgresql}
                      value={formData.pgHost}
                      onChange={(e) => setFormData({...formData, pgHost: e.target.value})}
                      className="input-field"
                      placeholder="ptbd01.viptecnologia.com.br"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Porta PostgreSQL
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="65535"
                      value={formData.pgPort}
                      onChange={(e) => setFormData({...formData, pgPort: parseInt(e.target.value)})}
                      className="input-field"
                      placeholder="5432"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Usuário PostgreSQL *
                    </label>
                    <input
                      type="text"
                      required={formData.backupPostgresql}
                      value={formData.pgUser}
                      onChange={(e) => setFormData({...formData, pgUser: e.target.value})}
                      className="input-field"
                      placeholder="postgres"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Senha PostgreSQL *
                    </label>
                    <input
                      type="password"
                      required={formData.backupPostgresql && !isEditing}
                      value={formData.pgPassword}
                      onChange={(e) => setFormData({...formData, pgPassword: e.target.value})}
                      className="input-field"
                      placeholder={isEditing ? "Deixe em branco para manter atual" : "senha-postgresql"}
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Tipos de Backup PostgreSQL</label>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.createDataBackup}
                        onChange={(e) => setFormData({...formData, createDataBackup: e.target.checked})}
                        className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                      />
                      <span className="ml-2 text-sm text-gray-700">Backup dos dados (--data-only)</span>
                    </label>

                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.createSchemaBackup}
                        onChange={(e) => setFormData({...formData, createSchemaBackup: e.target.checked})}
                        className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                      />
                      <span className="ml-2 text-sm text-gray-700">Backup do esquema (--schema-only)</span>
                    </label>

                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.createConsolidatedBackup}
                        onChange={(e) => setFormData({...formData, createConsolidatedBackup: e.target.checked})}
                        className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                      />
                      <span className="ml-2 text-sm text-gray-700">Backup consolidado (todos os bancos em um arquivo)</span>
                    </label>
                  </div>
                </div>
              </div>
            )}

            {/* Portainer Configuration */}
            {formData.backupPortainer && (
              <div className="card">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Configurações Portainer</h3>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Diretório de dados do Portainer
                  </label>
                  <input
                    type="text"
                    value={formData.portainerDataPath}
                    onChange={(e) => setFormData({...formData, portainerDataPath: e.target.value})}
                    className="input-field"
                    placeholder="/var/lib/docker/volumes/portainer_data/_data"
                  />
                </div>

                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.includePortainerDatabase}
                      onChange={(e) => setFormData({...formData, includePortainerDatabase: e.target.checked})}
                      className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                    />
                    <span className="ml-2 text-sm text-gray-700">Incluir banco de dados do Portainer</span>
                  </label>

                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.includePortainerStacks}
                      onChange={(e) => setFormData({...formData, includePortainerStacks: e.target.checked})}
                      className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                    />
                    <span className="ml-2 text-sm text-gray-700">Incluir stacks (docker-compose.yml)</span>
                  </label>

                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.includePortainerCerts}
                      onChange={(e) => setFormData({...formData, includePortainerCerts: e.target.checked})}
                      className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                    />
                    <span className="ml-2 text-sm text-gray-700">Incluir certificados SSL</span>
                  </label>
                </div>
              </div>
            )}

            {/* Advanced Configuration */}
            <div className="card">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Configurações Avançadas</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Caminho PostgreSQL no S3
                  </label>
                  <input
                    type="text"
                    value={formData.s3PostgresqlPath}
                    onChange={(e) => setFormData({...formData, s3PostgresqlPath: e.target.value})}
                    className="input-field"
                    placeholder="postgresql"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Caminho Portainer no S3
                  </label>
                  <input
                    type="text"
                    value={formData.s3PortainerPath}
                    onChange={(e) => setFormData({...formData, s3PortainerPath: e.target.value})}
                    className="input-field"
                    placeholder="portainer"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.useS3DateFolders}
                    onChange={(e) => setFormData({...formData, useS3DateFolders: e.target.checked})}
                    className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                  />
                  <span className="ml-2 text-sm text-gray-700">Organizar no S3 por pastas de data (YYYY/MM)</span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.removeLocalAfterS3Upload}
                    onChange={(e) => setFormData({...formData, removeLocalAfterS3Upload: e.target.checked})}
                    className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                  />
                  <span className="ml-2 text-sm text-gray-700">Remover backups locais após envio para S3</span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.autoCleanS3}
                    onChange={(e) => setFormData({...formData, autoCleanS3: e.target.checked})}
                    className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                  />
                  <span className="ml-2 text-sm text-gray-700">Limpar automaticamente backups antigos no S3</span>
                </label>
              </div>
            </div>

            {/* Email Configuration */}
            <div className="card">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Configurações de Email</h3>
              
              <div className="mb-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.emailEnabled}
                    onChange={(e) => setFormData({...formData, emailEnabled: e.target.checked})}
                    className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                  />
                  <span className="ml-2 text-sm text-gray-700">Ativar notificações por email</span>
                </label>
              </div>

              {formData.emailEnabled && (
                <div className="space-y-6">
                  {/* SMTP Configuration Section */}
                  <div>
                    <h4 className="text-md font-medium text-gray-800 mb-3">Configurações SMTP</h4>
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Email de destino *
                          </label>
                          <input
                            type="email"
                            required={formData.emailEnabled}
                            value={formData.emailTo}
                            onChange={(e) => setFormData({...formData, emailTo: e.target.value})}
                            className="input-field"
                            placeholder="admin@empresa.com"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Email remetente *
                          </label>
                          <input
                            type="email"
                            required={formData.emailEnabled}
                            value={formData.emailFrom}
                            onChange={(e) => setFormData({...formData, emailFrom: e.target.value})}
                            className="input-field"
                            placeholder="backup@empresa.com"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Servidor SMTP *
                          </label>
                          <input
                            type="text"
                            required={formData.emailEnabled}
                            value={formData.smtpHost}
                            onChange={(e) => setFormData({...formData, smtpHost: e.target.value})}
                            className="input-field"
                            placeholder="smtp.gmail.com"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Porta SMTP
                          </label>
                          <input
                            type="number"
                            min="1"
                            max="65535"
                            value={formData.smtpPort}
                            onChange={(e) => setFormData({...formData, smtpPort: parseInt(e.target.value)})}
                            className="input-field"
                            placeholder="587"
                          />
                        </div>

                        <div>
                          <label className="flex items-center mt-6">
                            <input
                              type="checkbox"
                              checked={formData.smtpUseSSL}
                              onChange={(e) => setFormData({...formData, smtpUseSSL: e.target.checked})}
                              className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                            />
                            <span className="ml-2 text-sm text-gray-700">Usar SSL/TLS</span>
                          </label>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Usuário SMTP
                          </label>
                          <input
                            type="text"
                            value={formData.smtpUser}
                            onChange={(e) => setFormData({...formData, smtpUser: e.target.value})}
                            className="input-field"
                            placeholder="usuario@empresa.com"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Senha SMTP
                          </label>
                          <input
                            type="password"
                            value={formData.smtpPassword}
                            onChange={(e) => setFormData({...formData, smtpPassword: e.target.value})}
                            className="input-field"
                            placeholder={isEditing ? "Deixe em branco para manter atual" : "senha-do-smtp"}
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={formData.notifyOnSuccess}
                            onChange={(e) => setFormData({...formData, notifyOnSuccess: e.target.checked})}
                            className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                          />
                          <span className="ml-2 text-sm text-gray-700">Notificar em caso de sucesso</span>
                        </label>

                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={formData.notifyOnFailure}
                            onChange={(e) => setFormData({...formData, notifyOnFailure: e.target.checked})}
                            className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                          />
                          <span className="ml-2 text-sm text-gray-700">Notificar em caso de falha</span>
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Email Template Section */}
                  <div>
                    <h4 className="text-md font-medium text-gray-800 mb-3">Template de Email</h4>
                    
                    <EmailTemplateSelector
                      selectedTemplate={formData.emailTemplate}
                      onTemplateSelect={(templateKey) => setFormData({...formData, emailTemplate: templateKey})}
                      required={formData.emailEnabled}
                    />
                    
                    {/* Variables Reference */}
                    <div className="mt-3 p-3 bg-gray-50 rounded-md">
                      <p className="text-sm font-medium text-gray-700 mb-2">Variáveis disponíveis nos templates:</p>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                        <div className="bg-white px-2 py-1 rounded border border-gray-200">
                          <code className="text-gray-600">{'{{date}}'}</code>
                        </div>
                        <div className="bg-white px-2 py-1 rounded border border-gray-200">
                          <code className="text-gray-600">{'{{hostname}}'}</code>
                        </div>
                        <div className="bg-white px-2 py-1 rounded border border-gray-200">
                          <code className="text-gray-600">{'{{status}}'}</code>
                        </div>
                        <div className="bg-white px-2 py-1 rounded border border-gray-200">
                          <code className="text-gray-600">{'{{totalFiles}}'}</code>
                        </div>
                        <div className="bg-white px-2 py-1 rounded border border-gray-200">
                          <code className="text-gray-600">{'{{databaseCount}}'}</code>
                        </div>
                        <div className="bg-white px-2 py-1 rounded border border-gray-200">
                          <code className="text-gray-600">{'{{portainerCount}}'}</code>
                        </div>
                        <div className="bg-white px-2 py-1 rounded border border-gray-200">
                          <code className="text-gray-600">{'{{awsBucket}}'}</code>
                        </div>
                        <div className="bg-white px-2 py-1 rounded border border-gray-200">
                          <code className="text-gray-600">{'{{uptime}}'}</code>
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        💡 Essas variáveis serão substituídas automaticamente pelos valores reais durante o envio do email.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Database Selection */}
            {formData.backupPostgresql && (
              <div className="card">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Seleção de Bancos PostgreSQL</h3>
                
                {loadingDatabases ? (
                  <p className="text-gray-500">Carregando bancos de dados...</p>
                ) : availableDatabases && Array.isArray(availableDatabases) && availableDatabases.length > 0 ? (
                  <div className="space-y-2">
                    {availableDatabases.map((db, index) => {
                      const currentDb = (formData.databases || []).find(d => 
                        (d.name === db.name) || (d.databaseName === db.name)
                      ) || db;
                      
                      return (
                        <div key={index} className="flex items-center space-x-4 p-2 bg-gray-50 rounded">
                          <label className="flex items-center flex-1">
                            <input
                              type="checkbox"
                              checked={currentDb.enabled || false}
                              onChange={(e) => handleDatabaseToggle(db.name, 'enabled', e.target.checked)}
                              className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                            />
                            <span className="ml-2 font-medium text-gray-700">{db.name}</span>
                          </label>
                          
                          <div className="flex space-x-2">
                            <label className="flex items-center text-xs">
                              <input
                                type="checkbox"
                                checked={currentDb.schemaOnly || false}
                                onChange={(e) => handleDatabaseToggle(db.name, 'schemaOnly', e.target.checked)}
                                disabled={!currentDb.enabled}
                                className="rounded border-gray-300 text-blue-600 text-xs"
                              />
                              <span className="ml-1 text-gray-600">Só esquema</span>
                            </label>
                            
                            <label className="flex items-center text-xs">
                              <input
                                type="checkbox"
                                checked={currentDb.dataOnly || false}
                                onChange={(e) => handleDatabaseToggle(db.name, 'dataOnly', e.target.checked)}
                                disabled={!currentDb.enabled}
                                className="rounded border-gray-300 text-blue-600 text-xs"
                              />
                              <span className="ml-1 text-gray-600">Só dados</span>
                            </label>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-gray-500">Nenhum banco PostgreSQL encontrado.</p>
                )}
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className="btn-secondary"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="btn-primary"
              >
                {isEditing ? 'Atualizar' : 'Criar'} Configuração
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Visual Email Template Editor Component
const EmailTemplateVisualEditor = ({ value, onChange }) => {
  const [visualData, setVisualData] = useState({
    title: 'Backup Status',
    subtitle: 'Data: {{date}}',
    mainMessage: 'Backup realizado com sucesso!',
    showStats: true,
    showDetails: true,
    showSystemInfo: true,
    footerMessage: 'Este é um e-mail automático gerado pelo sistema de backup.'
  });

  // Extract content from HTML template
  const parseHTMLTemplate = (html) => {
    if (!html) return;
    
    try {
      // Simple parsing to extract common elements
      const titleMatch = html.match(/<h1[^>]*>([^<]+)<\/h1>/);
      if (titleMatch) setVisualData(prev => ({ ...prev, title: titleMatch[1] }));
      
      const subtitleMatch = html.match(/<p[^>]*>([^<]+)<\/p>/);
      if (subtitleMatch) setVisualData(prev => ({ ...prev, subtitle: subtitleMatch[1] }));
    } catch (error) {
      console.error('Error parsing template:', error);
    }
  };

  // Generate HTML template from visual data
  const generateHTMLTemplate = () => {
    const template = `<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <style>
        body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 800px; margin: 0 auto; background-color: #f4f4f4; }
        .container { background-color: white; margin: 20px auto; border-radius: 10px; box-shadow: 0 0 10px rgba(0,0,0,0.1); overflow: hidden; }
        .header { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 30px; text-align: center; }
        .header h1 { margin: 0; font-size: 28px; }
        .header p { margin: 5px 0 0 0; opacity: 0.9; }
        .content { padding: 30px; }
        .success-banner { background-color: #d4edda; border: 1px solid #c3e6cb; color: #155724; padding: 15px; border-radius: 5px; margin-bottom: 20px; }
        .stats-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 20px; margin: 20px 0; }
        .stat-card { background-color: #f8f9fa; padding: 20px; border-radius: 8px; text-align: center; }
        .stat-value { font-size: 24px; font-weight: bold; color: #667eea; }
        .stat-label { color: #666; font-size: 14px; margin-top: 5px; }
        .system-info { margin-top: 30px; padding: 20px; background-color: #f8f9fa; border-radius: 8px; }
        .info-row { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #e9ecef; }
        .info-row:last-child { border-bottom: none; }
        .footer { background-color: #f8f9fa; padding: 20px; text-align: center; color: #666; font-size: 12px; }
    </style>
</head>
<body>
    <div class="container">
        <div class="header">
            <h1>🔒 ${visualData.title}</h1>
            <p>${visualData.subtitle}</p>
        </div>
        
        <div class="content">
            <div class="success-banner">
                ✅ <strong>${visualData.mainMessage}</strong><br>
                Total de arquivos gerados: {{totalFiles}}
            </div>
            ${visualData.showStats ? `
            <div class="stats-grid">
                <div class="stat-card">
                    <div class="stat-value">{{databaseCount}}</div>
                    <div class="stat-label">Bases de Dados</div>
                </div>
                <div class="stat-card">
                    <div class="stat-value">{{portainerCount}}</div>
                    <div class="stat-label">Backup Portainer</div>
                </div>
                <div class="stat-card">
                    <div class="stat-value">30 dias</div>
                    <div class="stat-label">Retenção no S3</div>
                </div>
            </div>` : ''}
            ${visualData.showSystemInfo ? `
            <div class="system-info">
                <h3>📊 Status do Servidor</h3>
                <div class="info-row">
                    <span><strong>Hostname:</strong></span>
                    <span>{{hostname}}</span>
                </div>
                <div class="info-row">
                    <span><strong>CPU:</strong></span>
                    <span>{{cpuUsage}} de {{cpuCores}} cores</span>
                </div>
                <div class="info-row">
                    <span><strong>Memória:</strong></span>
                    <span>{{memoryUsed}} / {{memoryTotal}} ({{memoryPercent}})</span>
                </div>
                <div class="info-row">
                    <span><strong>Uptime:</strong></span>
                    <span>{{uptime}}</span>
                </div>
            </div>` : ''}
        </div>
        
        <div class="footer">
            <p>${visualData.footerMessage}</p>
            <p>© {{year}} Sistema de Backup Automático</p>
        </div>
    </div>
</body>
</html>`;

    onChange(template);
  };

  const updateVisualData = (field, value) => {
    const newData = { ...visualData, [field]: value };
    setVisualData(newData);
    // Auto-generate HTML when visual data changes
    setTimeout(() => generateHTMLTemplate(), 100);
  };

  return (
    <div className="space-y-4">
      <div className="bg-blue-50 p-3 rounded-md">
        <p className="text-sm text-blue-800">
          📝 <strong>Editor Visual:</strong> Modifique o email abaixo sem se preocupar com HTML. 
          As variáveis (como {`{{date}}`} e {`{{hostname}}`}) serão automaticamente substituídas pelos valores reais.
        </p>
      </div>

      <div className="space-y-3">
        {/* Header Section */}
        <div className="border rounded-lg p-4">
          <h5 className="font-medium text-gray-800 mb-2">📧 Cabeçalho do Email</h5>
          <div className="space-y-2">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Título Principal</label>
              <input
                type="text"
                value={visualData.title}
                onChange={(e) => updateVisualData('title', e.target.value)}
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Ex: Backup Status"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Subtítulo</label>
              <input
                type="text"
                value={visualData.subtitle}
                onChange={(e) => updateVisualData('subtitle', e.target.value)}
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Ex: Data: {{date}}"
              />
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className="border rounded-lg p-4">
          <h5 className="font-medium text-gray-800 mb-2">📄 Conteúdo Principal</h5>
          <div className="space-y-2">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Mensagem Principal</label>
              <input
                type="text"
                value={visualData.mainMessage}
                onChange={(e) => updateVisualData('mainMessage', e.target.value)}
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Ex: Backup realizado com sucesso!"
              />
            </div>
          </div>
        </div>

        {/* Sections Toggle */}
        <div className="border rounded-lg p-4">
          <h5 className="font-medium text-gray-800 mb-2">🔧 Seções do Email</h5>
          <div className="space-y-2">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={visualData.showStats}
                onChange={(e) => updateVisualData('showStats', e.target.checked)}
                className="rounded border-gray-300 text-blue-600"
              />
              <span className="ml-2 text-sm text-gray-700">Mostrar estatísticas (contadores de backup)</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={visualData.showSystemInfo}
                onChange={(e) => updateVisualData('showSystemInfo', e.target.checked)}
                className="rounded border-gray-300 text-blue-600"
              />
              <span className="ml-2 text-sm text-gray-700">Mostrar informações do servidor</span>
            </label>
          </div>
        </div>

        {/* Footer Section */}
        <div className="border rounded-lg p-4">
          <h5 className="font-medium text-gray-800 mb-2">🔻 Rodapé</h5>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Mensagem do Rodapé</label>
            <input
              type="text"
              value={visualData.footerMessage}
              onChange={(e) => updateVisualData('footerMessage', e.target.value)}
              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Ex: Este é um e-mail automático"
            />
          </div>
        </div>

        {/* Preview */}
        <div className="border rounded-lg p-4 bg-gray-50">
          <h5 className="font-medium text-gray-800 mb-2">👁️ Pré-visualização</h5>
          <div className="bg-white p-4 rounded border text-sm">
            <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white p-4 rounded-t text-center">
              <h1 className="text-lg font-bold">🔒 {visualData.title}</h1>
              <p className="text-sm opacity-90">{visualData.subtitle}</p>
            </div>
            <div className="p-4">
              <div className="bg-green-100 border border-green-300 text-green-800 p-2 rounded mb-3">
                ✅ <strong>{visualData.mainMessage}</strong><br/>
                <small>Total de arquivos gerados: (será substituído pela variável)</small>
              </div>
              {visualData.showStats && (
                <div className="grid grid-cols-3 gap-2 mb-3">
                  <div className="bg-gray-100 p-2 rounded text-center text-xs">
                    <div className="font-bold text-blue-600">X</div>
                    <div>Bases de Dados</div>
                  </div>
                  <div className="bg-gray-100 p-2 rounded text-center text-xs">
                    <div className="font-bold text-blue-600">Y</div>
                    <div>Backup Portainer</div>
                  </div>
                  <div className="bg-gray-100 p-2 rounded text-center text-xs">
                    <div className="font-bold text-blue-600">30 dias</div>
                    <div>Retenção no S3</div>
                  </div>
                </div>
              )}
              {visualData.showSystemInfo && (
                <div className="bg-gray-100 p-3 rounded text-xs">
                  <h6 className="font-bold mb-2">📊 Status do Servidor</h6>
                  <div className="space-y-1">
                    <div className="flex justify-between">
                      <span><strong>Hostname:</strong></span>
                      <span>(hostname do servidor)</span>
                    </div>
                    <div className="flex justify-between">
                      <span><strong>CPU:</strong></span>
                      <span>(uso da CPU)</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="bg-gray-100 p-3 rounded-b text-center text-xs text-gray-600">
              {visualData.footerMessage}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BackupConfig;